<template>
  <Container class="spacing-top-mobile-xl spacing-top-desktop-l">
    <h2 v-if="title" class="tabbed-content_title" :class="{ 'tabbed-content_title--centered': !areTabsScrolling }">
      {{ title }}
    </h2>
    <div class="tabbed-content" :class="{ 'tabbed-content--scrolling': areTabsScrolling }">
      <div class="tabbed-content_tabs-container" ref="tabsContainer" @scroll="onScroll">
        <ul class="tabbed-content_tabs" role="tablist">
          <li class="tabbed-content_tab" role="presentation" v-for="(tab, index) in tabs" :key="tab.title">
            <button type="button" role="tab" :ref="(el) => updateTabRefs(el, index)"
              :tabindex="tab.hash === activeTabHash ? 0 : -1" :aria-selected="tab.hash === activeTabHash"
              :aria-controls="tab.id" :id="`tab_${tab.id}`" class="tabbed-content_tab-btn" @keydown="onKeyDown"
              @click="onClick(tab.hash, $event)" @focus="onFocus">
              <span>
                {{ tab.title }}
              </span>
            </button>
          </li>
        </ul>
      </div>
      <div class="tabbed-content_tabs-indictor" ref="indicatorTrack" @click="onIndicatorClick">
        <div class="tabbed-content_tabs-indictor-thumb" ref="indicatorThumb"></div>
      </div>
    </div>
  </Container>
  <slot></slot>
</template>

<script setup>
import {
  ref, provide, onMounted, onUnmounted, nextTick,
} from 'vue';
import Container from '@/components/layout/Container.vue';

defineProps({
  title: {
    type: String,
    required: true,
  },
});

const activeTabHash = ref(window.location.hash);
const tabs = ref([]);
const tabRefs = ref([]);
const tabsContainer = ref(null);
const areTabsScrolling = ref(false);
const indicatorThumb = ref(null);
const indicatorTrack = ref(null);

// workaround for bug in current vue version with refs in v-for loop
const updateTabRefs = (el, index) => {
  tabRefs.value[index] = el;
};

const activateTab = (index) => {
  const correctedIndex = Math.max(Math.min(index, tabs.value.length - 1), 0);
  activeTabHash.value = tabs.value[correctedIndex].hash;
  tabRefs.value[correctedIndex].focus();
};

const onFocus = (event) => {
  event.target.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
};

const onKeyDown = (event) => {
  if (!event || !event.key) return;

  if (event.key === 'Home') activateTab(0);

  if (event.key === 'End') activateTab(tabs.value.length - 1);

  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    activateTab(tabs.value.findIndex((tab) => tab.hash === activeTabHash.value) + 1);
  }

  if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    activateTab(tabs.value.findIndex((tab) => tab.hash === activeTabHash.value) - 1);
  }
};

const onClick = (hash, event) => {
  if (event) event.preventDefault();

  window.history.pushState(null, null, hash);
  activeTabHash.value = hash;
};

const onIndicatorClick = (event) => {
  const rect = indicatorTrack.value.getBoundingClientRect();
  const percentageOnTrack = ((event.clientX - rect.x) / rect.width);

  tabsContainer.value.scrollTo({
    top: 0,
    left: tabsContainer.value.scrollWidth * percentageOnTrack,
    behavior: 'auto',
  });
};

const onScroll = () => {
  const el = tabsContainer.value;
  const maxLeft = 100 - parseInt(indicatorThumb.value.style.width, 10);
  const scrolledPercentage = Math.round((el.scrollLeft / (el.scrollWidth - el.clientWidth)) * 100);
  const percentage = Math.round((maxLeft / 100) * scrolledPercentage);

  indicatorThumb.value.style.transform = `translateX(${(indicatorTrack.value.clientWidth / 100) * percentage}px)`;
};

const onResize = () => {
  const el = tabsContainer.value;
  const percentageVisible = Math.round((el.clientWidth / el.scrollWidth) * 100);

  areTabsScrolling.value = percentageVisible < 100;
  indicatorThumb.value.style.width = `${percentageVisible}%`;
};

const onHistoryChanged = () => {
  if (!window.location.hash) return;

  const index = tabs.value.findIndex((tab) => tab.hash === window.location.hash);

  if (index !== -1) activateTab(index);
};

provide('addTab', (tab) => {
  const count = tabs.value.push(tab);

  if (count === 1 && !activeTabHash.value) {
    activeTabHash.value = tab.hash;
  }
});
provide('activeTabHash', activeTabHash);

onMounted(async () => {
  if (!tabs.value.find((tab) => tab.hash === activeTabHash.value)) {
    activeTabHash.value = tabs.value[0].hash;
  }

  window.addEventListener('popstate', onHistoryChanged);
  window.addEventListener('resize', onResize, { passive: true });
  await nextTick();

  onResize();
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  window.removeEventListener('popstate', onHistoryChanged);
});
</script>
